import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

const LEGAL_DOCUMENTS = [
  { id: 'terms', label: 'Terms of Service' },
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'dpa', label: 'Data Processing Agreement (DPA)' },
  { id: 'aup', label: 'Acceptable Use Policy (AUP)' },
  { id: 'retention', label: 'Data Retention & Deletion Policy' },
  { id: 'disclaimer', label: 'Liability & IP Disclaimer' },
  { id: 'compliance', label: 'Compliance & Ethics Agreement' },
  { id: 'cookies', label: 'Cookie Policy' },
  { id: 'beta', label: 'Beta / Pre-Release Agreement' },
  { id: 'dmca', label: 'DMCA / Copyright Takedown Policy' },
];

const SUPPORTING_POLICIES = [
  { id: 'ethics-environment', label: 'Ethics & Environmental Commitment Policy' },
  { id: 'subprocessors', label: 'Sub-Processor List' },
  { id: 'ai-transparency', label: 'Technology & Data Processing Note' },
  { id: 'support-policy', label: 'Support Policy' },
  { id: 'refunds', label: 'Refund & Cancellation Policy' },
  { id: 'security', label: 'Security Overview / Trust Page' },
  
];

const POLICY_TEXTS = {
  terms: `<h2>Terms of Service</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<h3>1. Introduction</h3>
<p>These Terms of Service ("Terms") govern your access to and use of the Outlne website, platform, and interior design visualization services (collectively, the "Service"), provided by Outlne, Inc. ("Outlne," "we," "us," or "our"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.</p>

<h3>2. Eligibility and Account</h3>
<p><strong>2.1.</strong> You must be at least 18 years old to create an account. By signing up, you represent and warrant that you meet this age requirement. Any misrepresentation of your age or birthdate is a material breach of these Terms.</p>
<p><strong>2.2.</strong> You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account. You agree to notify us immediately of any unauthorized use.</p>
<p><strong>2.3.</strong> We reserve the right to suspend or terminate accounts that violate these Terms or pose a risk to Outlne or other users.</p>

<h3>3. Service Description</h3>
<p>Outlne is an interior design visualization platform that allows Users to upload a photo of any room and make edits through manual area selections or quick style transformations. Users simply snap a photo, draw/select what they want to change, and instantly preview new furniture, styles, and finishes in their actual space. The platform supports 3D models, architectural tools for measurements, floor plans, annotated exports, and assists in ultra‑realistic renders. Generated Content is produced through integrated generative processing technology for visualization planning purposes only. The Service is intended for residential and client commercial use, enabling Users to plan real‑world changes and present ideas to contractors, landlords, and/or condo boards before any physical modifications are made. The Service relies on third‑party processing providers and cloud infrastructure. Features may be added, modified, or discontinued at our discretion as set forth in Section 14.</p>

<h3>4. User Content and Intellectual Property</h3>
<p><strong>4.1. Your Content.</strong> You retain all ownership rights in the original images, designs, 3D models, textures, and other materials you upload ("User Content").</p>
<p><strong>4.2. License to Outlne.</strong> By uploading User Content, you grant Outlne a worldwide, non‑exclusive, royalty‑free, fully paid‑up license to host, store, reproduce, modify (solely as necessary to provide the Service, e.g., resizing, format conversion), and display your User Content. This license ends when you delete your User Content and your account, except as retained in backups for a reasonable period (up to 90 days).</p>
<p><strong>4.3. Photo/Video Permission.</strong> Before uploading any photo or video, you must confirm that you have the right to use it and accept full liability for any copyright or other claims. This confirmation forms part of your representations under Section 4.5.</p>
<p><strong>4.4. Generated Content.</strong> Subject to your compliance with these Terms and payment of applicable fees, Outlne grants you a non‑exclusive, perpetual, worldwide, royalty‑free license to use, reproduce, display, and create derivative works of the generative outputs ("Generated Content") for both personal and commercial purposes, including client projects, portfolios, and marketing materials. You are free to transfer ownership of Generated Content to your clients; Outlne does not mediate or manage such transfers.</p>
<p><strong>4.5. Prohibited Content.</strong> You may not upload User Content that:</p>
<ul>
  <li>a) Infringes any third‑party intellectual property, privacy, or publicity right;</li>
  <li>b) Is unlawful, harassing, defamatory, obscene, or promotes violence;</li>
  <li>c) Contains malicious code or viruses;</li>
  <li>d) Depicts minors in a harmful or inappropriate manner.</li>
</ul>
<p><strong>4.6. User Warranties.</strong> You represent and warrant that you own or have secured all necessary licenses, consents, and permissions for the User Content you upload, and that such content does not violate any law or third‑party rights. You will indemnify Outlne for any claims, damages, liabilities, and expenses (including reasonable attorneys' fees) arising from your breach of this warranty.</p>

<h3>5. Acceptable Use</h3>
<p>You agree not to:</p>
<ul>
  <li>Reverse engineer, decompile, or attempt to extract the source code of the Service;</li>
  <li>Use the Service to develop a competing product;</li>
  <li>Bypass any security measures or access non‑public areas of the Service;</li>
  <li>Use the Service to generate spam, disinformation, deepfakes intended to deceive, or any content that violates our Acceptable Use Policy;</li>
  <li>Resell, sublicense, or otherwise commercially exploit the Service itself (as opposed to Generated Content) without a separate written agreement with Outlne.</li>
</ul>

<h3>6. Payments, Subscriptions, and Refunds</h3>
<p><strong>6.1. Fees.</strong> Certain features require paid subscriptions. All fees are non‑refundable except as required by applicable law.</p>
<p><strong>6.2. Plans.</strong> Our subscription plans and their recurring fees are published on our website. As of the Last Updated date, the plans include:</p>
<ul>
  <li><strong>Trial:</strong> free, limited generation budget (~100 iterations or 2–3 videos, combined pool). Once exhausted, read‑only access — you may view/delete projects but not generate or export.</li>
  <li><strong>Creator:</strong> $45/month, includes a base monthly generation budget of 342 iterations OR 12 videos (combined pool). Top‑up available up to $100 per billing cycle.</li>
  <li><strong>Pro:</strong> $100/month, includes a base monthly generation budget of 765 iterations OR 27 videos (combined pool). Top‑up available up to $1,000 per billing cycle.</li>
</ul>
<p><strong>6.3. Generation Budget Mechanics.</strong> One combined pool of iterations and videos. When exhausted, you may upgrade your plan or add balance (top‑up). Base monthly allotments do not roll over; top‑up balances roll over indefinitely. Top‑up balances are non‑refundable, as the corresponding processing capacity is loaded and reserved on third‑party infrastructure immediately upon purchase, incurring non‑recoverable costs.</p>
<p><strong>6.4. Billing.</strong> Subscription fees are billed in advance on a recurring basis (monthly or annually as applicable) and will auto‑renew until canceled. You may cancel at any time through account settings; cancellation takes effect at the end of the current billing period. No refunds for partial periods.</p>
<p><strong>6.5. Price Changes.</strong> We may change fees upon 30 days' notice. Continued use after the change constitutes acceptance.</p>

<h3>7. Third‑Party Services and Processing</h3>
<p>The Service integrates third‑party processing APIs, cloud hosting, image providers (including Unsplash), and payment processors. Outlne is not responsible for the availability or output of these third‑party services. Your use of Generated Content may be subject to additional terms of the underlying processing model licenses. You agree to comply with all applicable third‑party terms. Outlne does not guarantee that processing providers will not use your content for model improvement; we select vendors based on privacy protectiveness but have not obtained contractual no‑use assurances from all providers. See our Privacy Policy and Technology & Data Processing Note.</p>

<h3>8. Privacy</h3>
<p>Our collection and use of personal information is governed by our Privacy Policy, which is incorporated by reference.</p>

<h3>9. Disclaimer of Warranties</h3>
<p><strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON‑INFRINGEMENT. OUTLNE DOES NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR‑FREE, OR SECURE, NOR THAT GENERATED CONTENT WILL MEET YOUR REQUIREMENTS OR BE ACCURATE.</strong></p>

<h3>10. Limitation of Liability</h3>
<p><strong>TO THE FULLEST EXTENT PERMITTED BY LAW, OUTLNE, ITS AFFILIATES, OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. IN ANY EVENT, OUTLNE'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM. THE FOREGOING LIMITATIONS APPLY EVEN IF THE REMEDY FAILS OF ITS ESSENTIAL PURPOSE.</strong></p>

<h3>11. Indemnification</h3>
<p>You agree to indemnify, defend, and hold harmless Outlne from any claims, damages, liabilities, and expenses (including reasonable attorneys' fees) arising out of: (a) your User Content; (b) your use of the Service in violation of these Terms; (c) your violation of any third‑party right; or (d) your failure to obtain necessary approvals before implementing any design in the real world.</p>

<h3>12. Termination</h3>
<p>We may terminate or suspend your account at any time, with or without cause, upon notice. Upon termination, your right to use the Service ceases immediately. Provisions that by their nature should survive termination (e.g., IP, disclaimers, indemnification) shall survive.</p>

<h3>13. Governing Law and Disputes</h3>
<p>These Terms shall be governed by the laws of the State of California, without regard to conflict‑of‑law principles. Any dispute shall be resolved exclusively in the state or federal courts located in Los Angeles County, California. You waive any objection to personal jurisdiction and venue.</p>

<h3>14. Changes to Terms</h3>
<p>We may modify these Terms at any time. We will notify you of material changes via email or through the Service. Your continued use after the effective date constitutes acceptance of the revised Terms.</p>

<h3>15. Contact</h3>
<p>Email: legal@outlne.com</p>`,

  privacy: `<h2>Privacy Policy</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<h3>1. Introduction</h3>
<p>Outlne, Inc. ("Outlne," "we," "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our Service.</p>

<h3>2. Information We Collect</h3>
<p><strong>2.1. Information You Provide:</strong></p>
<ul>
  <li>Account data: first name, last name, email address, birthday, password (encrypted), home type (apartment/condo/house).</li>
  <li>User Content: photographs, motion path videos, 3D models, textures, text prompts, project descriptions.</li>
  <li>Payment information: payment card details are processed by our PCI‑compliant payment processor; we do not store full card numbers.</li>
</ul>
<p><strong>2.2. Automatically Collected Information:</strong></p>
<ul>
  <li>Device and usage data: IP address, browser type, operating system, referral URLs, pages viewed, feature usage, and error logs.</li>
  <li>Cookies and similar technologies (see Cookie Policy).</li>
</ul>
<p><strong>2.3. Generative Processing Data:</strong></p>
<ul>
  <li>When you use our visualization tools, your full photo and a mask image (generated from your selection) are transmitted to third‑party processing providers. For video rendering, your motion path video (a recording of a real physical space) is also transmitted. These providers process data in memory; we have selected privacy‑protective vendors but have not obtained contractual guarantees against training use. See our Data Processing Agreement.</li>
  <li>Voice input from Speak and Create is converted to text entirely on‑device; audio never leaves your device and is not uploaded to Outlne or any third party.</li>
</ul>

<h3>3. How We Use Your Information</h3>
<p>We use your information to:</p>
<ul>
  <li>Provide, maintain, and improve the Service;</li>
  <li>Process transactions and manage your generation budget;</li>
  <li>Send administrative messages (security alerts, account notices);</li>
  <li>Personalize your experience and provide customer support;</li>
  <li>Analyze usage patterns to develop new features;</li>
  <li>Detect, prevent, and address fraud, abuse, and security incidents;</li>
  <li>Comply with legal obligations.</li>
</ul>

<h3>4. Legal Bases (for UK/EEA Users)</h3>
<p>For users in the UK or European Economic Area, we rely on the following legal bases:</p>
<ul>
  <li>Performance of a contract (providing the Service);</li>
  <li>Legitimate interests (improving the Service, securing the platform);</li>
  <li>Consent (for non‑essential cookies and marketing);</li>
  <li>Compliance with legal obligations.</li>
</ul>

<h3>5. Data Sharing and Disclosure</h3>
<p><strong>5.1. Service Providers:</strong> We share data with trusted third‑party vendors who perform services on our behalf, including:</p>
<ul>
  <li>Cloud hosting and storage (Firebase/Google Cloud)</li>
  <li>Payment processing (Stripe)</li>
  <li>Image and video processing (third‑party GPU cloud providers, e.g., Replicate and others; full list available under NDA)</li>
  <li>Image search (Unsplash)</li>
  <li>Email delivery</li>
  <li>Analytics (if enabled)</li>
</ul>
<p>These providers are contractually bound to process data only as instructed and in compliance with applicable data protection laws.</p>
<p><strong>5.2. Legal Compliance:</strong> We may disclose information if required by law, subpoena, or government request.</p>
<p><strong>5.3. Business Transfers:</strong> In the event of a merger, acquisition, or asset sale, your data may be transferred, provided the recipient agrees to honor this Privacy Policy.</p>
<p><strong>5.4. Aggregated/De‑Identified Data:</strong> We may share non‑identifiable, aggregated data for research or marketing.</p>

<h3>6. International Data Transfers</h3>
<p>Your data may be transferred to and processed in the United States and other countries. For transfers from the UK/EEA, we ensure appropriate safeguards, such as Standard Contractual Clauses (SCCs) or the UK International Data Transfer Agreement. You may request a copy of these safeguards by contacting us.</p>

<h3>7. Data Retention</h3>
<p>We retain personal data only as long as necessary to fulfill the purposes outlined in this policy. Specific retention periods are detailed in our Data Retention and Deletion Policy.</p>

<h3>8. Your Rights</h3>
<p>Depending on your jurisdiction, you may have the right to:</p>
<ul>
  <li>Access, correct, or delete your personal data;</li>
  <li>Object to or restrict processing;</li>
  <li>Data portability;</li>
  <li>Withdraw consent (where processing is based on consent);</li>
  <li>Opt out of the sale/sharing of personal information (California residents under CCPA);</li>
  <li>File a complaint with a supervisory authority.</li>
</ul>
<p>To exercise your rights, email privacy@outlne.com. We will respond within legally required timeframes.</p>

<h3>9. Cookies and Tracking</h3>
<p>We use essential cookies for authentication and security. With your consent (where required, e.g., UK/EEA), we may also use analytics cookies. You can manage cookie preferences through your browser settings. See our Cookie Policy.</p>

<h3>10. Children's Privacy</h3>
<p>The Service is not directed to individuals under 18. We do not knowingly collect data from children. If we become aware of such collection, we will delete it promptly.</p>

<h3>11. Security</h3>
<p>We implement appropriate technical and organizational measures, including encryption at rest and in transit, access controls, and regular audits. However, no method is 100% secure.</p>

<h3>12. Changes to This Policy</h3>
<p>We will notify you of material updates via email or in‑app notice. Continued use after changes become effective constitutes acceptance.</p>

<h3>13. Contact Us</h3>
<p>Email: privacy@outlne.com</p>`,

  dpa: `<h2>Data Processing Agreement (DPA)</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<p>This Data Processing Agreement ("DPA") forms part of the Terms of Service between Outlne, Inc. ("Processor") and the customer using the Service ("Controller").</p>

<h3>1. Definitions</h3>
<p>"Personal Data" means any information relating to an identified or identifiable natural person processed by Processor on behalf of Controller under the Terms.</p>

<h3>2. Processing Details</h3>
<p><strong>2.1. Subject Matter:</strong> Provision of the Service as described in the Terms.</p>
<p><strong>2.2. Duration:</strong> For the term of the Controller's subscription plus any post‑termination retention period.</p>
<p><strong>2.3. Nature and Purpose:</strong> Hosting, storage, image and video processing via integrated third‑party generative providers, and related support.</p>
<p><strong>2.4. Types of Personal Data:</strong> Account information (name, email, birthday), User Content (images, videos, text prompts, 3D models), and mask images.</p>
<p><strong>2.5. Categories of Data Subjects:</strong> Controller's end users and any individuals whose data appears in User Content.</p>

<h3>3. Obligations of Processor</h3>
<p><strong>3.1.</strong> Processor shall process Personal Data only on documented instructions from Controller.</p>
<p><strong>3.2.</strong> Processor shall ensure that persons authorized to process Personal Data are bound by confidentiality obligations.</p>
<p><strong>3.3.</strong> Processor shall implement appropriate technical and organizational measures (see Appendix 1).</p>
<p><strong>3.4.</strong> Processor shall notify Controller without undue delay upon becoming aware of a personal data breach.</p>
<p><strong>3.5.</strong> Processor shall reasonably assist Controller in fulfilling data subject requests and data protection impact assessments.</p>
<p><strong>3.6.</strong> Processor shall engage sub‑processors only under a written contract imposing equivalent obligations. A list of sub‑processor categories is available in the Sub‑Processor List. Processor will inform Controller of any new sub‑processors, allowing an opportunity to object.</p>

<h3>4. International Transfers</h3>
<p>Processor may transfer Personal Data to the United States. Such transfers are subject to the EU Standard Contractual Clauses (SCCs) or UK International Data Transfer Agreement, as applicable, which are incorporated by reference.</p>

<h3>5. Deletion or Return of Data</h3>
<p>Upon termination, Processor shall, at Controller's choice, delete or return all Personal Data, except where retention is required by law. Deletion will follow the Data Retention and Deletion Policy.</p>

<h3>6. Liability</h3>
<p>Each party's liability under this DPA shall be governed by the limitation of liability provisions in the Terms.</p>

<h3>7. Governing Law</h3>
<p>This DPA is governed by the law specified in the Terms.</p>

<h3>Appendix 1 – Technical and Organizational Measures</h3>
<ul>
  <li>Encryption at rest (AES‑256) and in transit (TLS 1.2+)</li>
  <li>Role‑based access controls with multi‑factor authentication</li>
  <li>Regular vulnerability scanning and penetration testing</li>
  <li>Incident response plan</li>
  <li>Data minimization and pseudonymization where feasible</li>
</ul>`,

  aup: `<h2>Acceptable Use Policy (AUP)</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<p>This Acceptable Use Policy describes prohibited conduct when using Outlne's Service. You may not:</p>

<h3>1. Upload, generate, or distribute any content that:</h3>
<ul>
  <li>Is illegal, fraudulent, or promotes illegal activity;</li>
  <li>Infringes intellectual property, privacy, or publicity rights of others;</li>
  <li>Is obscene, pornographic, or depicts sexual exploitation;</li>
  <li>Incites violence, hatred, or discrimination based on race, ethnicity, religion, gender, sexual orientation, disability, or age;</li>
  <li>Contains malicious code, viruses, or is intended to damage or disrupt systems;</li>
  <li>Constitutes unauthorized advertising, spam, or phishing.</li>
</ul>

<h3>2. Use the Service to:</h3>
<ul>
  <li>Create deepfakes intended to harass, defame, or impersonate others without clear disclosure;</li>
  <li>Generate child sexual abuse material (CSAM) or any content that exploits minors;</li>
  <li>Engage in any activity that violates export control laws or sanctions.</li>
</ul>

<h3>3. System Abuse:</h3>
<ul>
  <li>Attempt to probe, scan, or test the vulnerability of the Service;</li>
  <li>Bypass any rate limits, usage quotas, or security measures;</li>
  <li>Use automated means (bots, scrapers) without our prior written consent.</li>
</ul>

<p>We reserve the right to suspend or terminate accounts that violate this AUP and to report illegal content to law enforcement. Report violations to abuse@outlne.com.</p>`,

  retention: `<h2>Data Retention & Deletion Policy</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<h3>1. Purpose</h3>
<p>This policy explains how long Outlne retains personal data and how deletion requests are handled.</p>

<h3>2. Retention Periods</h3>
<ul>
  <li><strong>Account Information (name, email, birthday):</strong> Retained for the life of the account plus 30 days after account deletion, unless a longer period is required by law.</li>
  <li><strong>User Content (images, videos, 3D models, projects):</strong> Active projects are stored until deleted by the user. When a project is deleted, it is soft‑deleted (status: 'deleted') and placed in a recoverable trash state for 30 days. During this period, you may restore the project. After 30 days, the project and all associated content (including all generated variations, layers, and assets) are permanently and irreversibly purged from our production systems. Backups may contain residual data for up to an additional 90 days, but will not be accessible or restorable.</li>
  <li><strong>Generative Processing Data:</strong> Images and mask images sent to third‑party providers are processed in memory and are not persistently stored by those providers. Temporary cache copies in Outlne's systems expire after 30 days. All generative variations (up to three per generation) are stored as part of your project's layers until you delete the project.</li>
  <li><strong>Transaction Records:</strong> Retained for 7 years to comply with tax and accounting regulations.</li>
  <li><strong>Logs and Analytics Data:</strong> Retained for 12 months, then anonymized or deleted.</li>
</ul>

<h3>3. Deletion Requests</h3>
<p>You may delete your account and associated data at any time via account settings. To request manual deletion or exercise data subject rights, contact privacy@outlne.com. We will respond within 30 days.</p>

<h3>4. Exceptions</h3>
<p>We may retain information if required by legal process, to enforce our Terms, or to prevent fraud.</p>`,

  disclaimer: `<h2>Liability & IP Disclaimer</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<h3>1. No Professional Advice</h3>
<p>Outlne is professional design software, not an architecture or engineering firm. Generated Content is produced by automated generative processing for visualization planning purposes only. It does not constitute structural, architectural, or engineering advice. You are solely responsible for verifying the suitability, safety, and legality of any design before physical implementation.</p>

<h3>2. Output Limitations</h3>
<p>Generative outputs may contain errors, artifacts, or unintended results. Because the underlying processing technology is trained on large datasets, outputs may inadvertently resemble existing works. Outlne makes no warranty as to the originality, uniqueness, or non‑infringement of any Generated Content. You accept full responsibility for vetting outputs and obtaining any necessary rights clearances before commercial use.</p>

<h3>3. Portfolio and Commercial Use</h3>
<p>We encourage you to use Generated Content in your professional portfolio, client presentations, and marketing materials. However, you acknowledge that any commercial reliance on such generative designs is at your own risk. You agree to indemnify Outlne against any third‑party claims arising from your use of Generated Content, including claims of copyright infringement or design defect.</p>

<h3>4. Real‑World Implementation</h3>
<ul>
  <li><strong>Cosmetic/Low‑Risk Changes:</strong> For superficial alterations (e.g., paint color, furniture arrangement), you are responsible for obtaining any necessary permissions (e.g., landlord, condo board) before implementation.</li>
  <li><strong>Structural/Higher‑Risk Changes:</strong> For any design affecting structural elements, load‑bearing walls, electrical, plumbing, or egress, you must obtain review and approval from a licensed architect, structural engineer, or other qualified professional. You are responsible for securing all required building permits. Outlne provides no assurance that Generated Content complies with building codes, safety standards, or zoning laws.</li>
  <li>By using architectural features (when available), you will be required to confirm your understanding of this obligation. Failure to obtain professional approvals is solely your liability.</li>
</ul>

<h3>5. Third‑Party Processing</h3>
<p>The Service relies on third‑party generative processing providers. Outlne does not own or control these technologies and has not obtained contractual indemnification from providers regarding the intellectual property used to develop them. Accordingly, Outlne disclaims any liability for claims arising from the use of such providers. This disclaimer is in addition to, and not in limitation of, the limitations of liability in the Terms of Service.</p>

<h3>6. IP Ownership</h3>
<p>Except for the limited license to Generated Content granted in the Terms, all rights, title, and interest in the Service, including its technology, UI, branding, and proprietary algorithms, remain exclusively with Outlne.</p>`,

  compliance: `<h2>Compliance & Ethics Agreement</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<h3>1. Our Commitment</h3>
<p>Outlne is committed to conducting business ethically and in compliance with all applicable laws, including anti‑corruption, sanctions, data protection, and human rights laws.</p>

<h3>2. User Obligations</h3>
<p>By using the Service, you agree to:</p>
<ul>
  <li>Comply with all applicable laws and regulations in your jurisdiction;</li>
  <li>Not use the Service for any purpose that would violate trade sanctions, export controls, or anti‑money laundering rules;</li>
  <li>Not upload or process any data that would cause Outlne to violate data protection laws;</li>
  <li>Use the Service in a manner consistent with our Acceptable Use Policy and these ethical standards.</li>
</ul>

<h3>3. Technology Ethics</h3>
<p>We design our generative editing features to respect user privacy and mitigate harmful bias to the extent technically feasible. We do not intentionally use personal data to further train models without consent. If you believe our technology produces biased or harmful outputs, report it to ethics@outlne.com.</p>

<h3>4. Reporting Misconduct</h3>
<p>If you become aware of any violation of this Agreement, report it promptly. We will investigate and take appropriate action, up to account termination.</p>`,

  cookies: `<h2>Cookie Policy</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<h3>1. What Are Cookies</h3>
<p>Cookies are small text files placed on your device. Similar technologies include local storage, pixels, and web beacons.</p>

<h3>2. How Outlne Uses Cookies</h3>
<p>We use cookies to:</p>
<ul>
  <li>Authenticate your session and keep you logged in (strictly necessary);</li>
  <li>Remember your preferences;</li>
  <li>Analyze how you use the Service (if you consent);</li>
  <li>Ensure security.</li>
</ul>

<h3>3. Cookie Categories</h3>
<ul>
  <li><strong>a) Strictly Necessary Cookies:</strong> Essential for the Service. Example: Firebase Auth session identifiers.</li>
  <li><strong>b) Performance and Analytics Cookies:</strong> With consent, we collect aggregated usage data. We may use Google Analytics or similar. No personally identifiable information is stored.</li>
  <li><strong>c) Functional Cookies:</strong> Not currently used beyond what is strictly necessary.</li>
</ul>

<h3>4. Third‑Party Cookies</h3>
<p>Service providers (Firebase, Stripe) may place their own cookies. Please review their policies.</p>

<h3>5. Your Choices</h3>
<p>You can block or delete cookies via browser settings. For UK/EEA users, a consent banner appears on first visit. You can change preferences via "Cookie Settings" in the footer.</p>

<h3>6. Updates</h3>
<p>We will post changes here and, for material updates, notify you as described in our Privacy Policy.</p>

<h3>7. Contact</h3>
<p>privacy@outlne.com</p>`,

  beta: `<h2>Beta / Pre‑Release Agreement</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<p>This Beta Testing and Pre‑Release Agreement is between Outlne, Inc. and you ("Tester"). By participating in any beta, alpha, or early access program for the Beta Service, you agree to these terms.</p>

<h3>1. Nature of Beta Service</h3>
<p>The Beta Service is pre‑release, experimental, and may contain bugs, errors, and incomplete features. It is provided for evaluation and feedback only — not for production use.</p>

<h3>2. Access and License</h3>
<p>Limited, non‑exclusive, non‑transferable, revocable license to access and use the Beta Service during the program. No resale or sublicense.</p>

<h3>3. Feedback</h3>
<p>You agree to provide honest feedback. You grant Outlne a perpetual, irrevocable, worldwide, royalty‑free license to use and incorporate Feedback into any product without compensation.</p>

<h3>4. Confidentiality</h3>
<p>The Beta Service and related communications are Confidential Information. Do not disclose, demonstrate, or publish without prior written consent. Use only for evaluation. This obligation survives termination for three years.</p>

<h3>5. Intellectual Property</h3>
<p>All rights in the Beta Service and improvements derived from Feedback remain exclusively with Outlne.</p>

<h3>6. Disclaimer of Warranties</h3>
<p><strong>THE BETA SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITH ALL FAULTS, WITHOUT ANY WARRANTY OF ANY KIND.</strong></p>

<h3>7. Limitation of Liability</h3>
<p><strong>TO THE FULLEST EXTENT PERMITTED BY LAW, OUTLNE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE BETA SERVICE. TOTAL LIABILITY SHALL NOT EXCEED FIFTY DOLLARS ($50) OR THE AMOUNT YOU PAID FOR THE BETA SERVICE, WHICHEVER IS LESS.</strong></p>

<h3>8. Termination</h3>
<p>Either party may terminate at any time, with or without cause, upon notice. Upon termination, cease all use and delete Confidential Information.</p>

<h3>9. No Obligation to Launch</h3>
<p>Outlne is not obligated to release a final version or continue the Beta Program.</p>

<h3>10. Governing Law</h3>
<p>California law. Disputes in Los Angeles County, California.</p>

<h3>11. General</h3>
<p>Entire agreement. If any provision is unenforceable, the rest remain effective.</p>

<p>By using the Beta Service, you acknowledge acceptance of this Agreement.</p>`,

  dmca: `<h2>DMCA / Copyright Takedown Policy</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<p>Outlne respects the intellectual property of others. If you believe that any content hosted on our Service infringes your copyright, please send a notice to our designated DMCA agent at copyright@outlne.com with:</p>
<ul>
  <li>A description of the copyrighted work</li>
  <li>A description of where the infringing material is located</li>
  <li>Your contact information</li>
  <li>A statement of good faith belief that the use is not authorized</li>
  <li>A statement, under penalty of perjury, that the information is accurate</li>
</ul>

<p>Upon receipt of a valid notice, we will promptly remove or disable access to the material and notify the user who posted it. Users who repeatedly infringe copyright may have their accounts terminated.</p>

<p><strong>Designated Agent:</strong> Outlne, Inc.<br>Email: copyright@outlne.com<br>Physical address: Provided upon request (no public office).</p>`,

  subprocessors: `<h2>Sub‑Processor List</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<p>This page lists the categories of third‑party data processors that Outlne engages to deliver the Service. Specific vendor names are provided under NDA upon request.</p>

<ul>
  <li><strong>Cloud Hosting & Storage:</strong> Infrastructure provider (USA) – hosts and stores account data, User Content, projects, and assets.</li>
  <li><strong>Image & Video Generative Processing:</strong> Third‑party GPU cloud providers – process images, mask images, and motion path videos for design transformations.</li>
  <li><strong>Payment Processing:</strong> PCI‑compliant provider – processes payment card transactions.</li>
  <li><strong>Image Search (Inspiration Gallery):</strong> Unsplash – receives search queries only; displays third‑party images with attribution.</li>
  <li><strong>Email & Communications:</strong> Cloud email delivery service – sends transactional emails.</li>
  <li><strong>Analytics & Error Tracking:</strong> Privacy‑focused analytics tool – collects usage statistics and crash reports (if enabled).</li>
</ul>

<p>All sub‑processors are bound by data processing agreements imposing equivalent data protection obligations. We will notify users of any new sub‑processor categories and provide an opportunity to object.</p>`,

  'ai-transparency': `<h2>Technology & Data Processing Note</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<p>Outlne uses third‑party generative processing technologies to assist in transforming images you upload. These systems have been trained on large datasets of publicly available images and can produce realistic design alterations based on your manual selections.</p>

<h3>What our technology does:</h3>
<ul>
  <li>Enables you to modify interior spaces by replacing furniture, wall colors, and finishes through manual area selection or Quick Style transformations.</li>
  <li>Estimates depth from 2D images for 3D‑aware edits.</li>
  <li>Generates video walkthroughs from still images and motion path recordings.</li>
  <li>Converts voice to text on‑device (Speak and Create); no audio is ever sent to servers.</li>
</ul>

<h3>How data is used:</h3>
<p>When you make a selection and generate, the full image and a mask image (derived from your lasso/pin selection) are sent to a generative processing provider. For motion path videos, the video is also sent. The provider processes these inputs and returns variations based on your instructions.</p>

<h3>Important limitations:</h3>
<ul>
  <li>Outputs are for visualization planning only and may contain artifacts or unintended results.</li>
  <li>Outputs may unintentionally resemble existing copyrighted works.</li>
  <li>The technology does not assess structural safety or building codes; any design must be reviewed by a qualified professional before real‑world physical changes are made.</li>
</ul>

<h3>Our commitment:</h3>
<ul>
  <li>We do not use your uploaded content to train or fine‑tune the processing models ourselves.</li>
  <li>We select vendors based on privacy protectiveness; however, we have not obtained contractual guarantees that providers will never use data for model improvement. We monitor their policies and rotate vendors as needed.</li>
  <li>All processing is on a per‑request basis; outputs are stored in your project until you delete them.</li>
  <li>We continuously evaluate our providers for bias, fairness, and privacy compliance.</li>
</ul>

<p>For further technical details, contact privacy@outlne.com.</p>`,

  'support-policy': `<h2>Support Policy</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<p>Outlne provides support via email at support@outlne.com during business hours (Monday–Friday, 9am–5pm Pacific Time). We strive to respond within one business day.</p>

<p>We make every effort to maintain platform availability, but the Service is provided on an "as available" basis and we do not guarantee uninterrupted access. Scheduled maintenance will be announced in advance when feasible.</p>

<p>This is not a Service Level Agreement and does not create binding uptime or response‑time guarantees.</p>`,

  refunds: `<h2>Refund & Cancellation Policy</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<h3>General No‑Refund Policy</h3>
<p>All purchases of Outlne subscriptions and generation budget top‑ups are final and non‑refundable, except as required by applicable law.</p>

<h3>Why no refunds?</h3>
<p>When you subscribe or add top‑up balance, your generation budget is loaded instantly, and the corresponding processing capacity is reserved on third‑party infrastructure. Because we incur immediate, non‑recoverable third‑party compute costs on your behalf, we cannot process refunds for used or partially used budgets.</p>

<h3>Top‑Up Balances</h3>
<p>Custom top‑up balances are non‑refundable for the same reason: upon purchase, the balance is allocated and reserved, incurring costs. Top‑up balances roll over indefinitely and are not tied to any billing cycle, but they cannot be refunded.</p>

<h3>Cancellation</h3>
<p>You may cancel your subscription at any time via account settings. Cancellation stops future billing but does not entitle you to a refund for the current billing period. You will retain access for the remainder of the period you paid for.</p>

<h3>Trial & Exceptions</h3>
<p>If a technical issue prevents you from using the Service, contact support@outlne.com. At our sole discretion, we may provide account credit or an extension.</p>

<h3>Statutory Rights</h3>
<p>This policy does not override any statutory cooling‑off rights you may have under local consumer protection laws (e.g., UK 14‑day cancellation for digital content if you have not started using the Service).</p>`,

  security: `<h2>Security Overview / Trust Page</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<p>At Outlne, protecting your data is a top priority. We have implemented the following security measures:</p>

<h3>Data Encryption:</h3>
<ul>
  <li>Data in transit: TLS 1.2 or higher</li>
  <li>Data at rest: AES‑256 encryption</li>
</ul>

<h3>Access Controls:</h3>
<ul>
  <li>Only authorized personnel have access to production systems, on a need‑to‑know basis</li>
  <li>Multi‑factor authentication required for administrative access</li>
  <li>Detailed access logs reviewed regularly</li>
</ul>

<h3>Infrastructure Security:</h3>
<ul>
  <li>Our infrastructure runs on a leading cloud provider with SOC 2, ISO 27001, and PCI DSS certifications</li>
  <li>Network firewalls, intrusion detection, and DDoS protection in place</li>
  <li>Regular vulnerability scans and penetration testing performed</li>
</ul>

<h3>User Data Isolation:</h3>
<ul>
  <li>Each user's project data is logically separated and accessible only by that user</li>
  <li>No User Content is shared between accounts or used for any purpose other than providing the Service</li>
</ul>

<h3>Compliance:</h3>
<ul>
  <li>We adhere to the principles of GDPR, UK GDPR, and CCPA</li>
  <li>We maintain a Data Processing Agreement for business customers (available upon request)</li>
  <li>We do not sell or rent personal data to third parties</li>
</ul>

<h3>Incident Response:</h3>
<ul>
  <li>Documented incident response plan</li>
  <li>In the event of a data breach affecting your account, we will notify you promptly in accordance with applicable law</li>
</ul>

<p>For additional security documentation or a third‑party security assessment, contact security@outlne.com.</p>`,

  'ethics-environment': `<h2>Ethics & Environmental Commitment Policy</h2>
<p class="last-updated">Last Updated: August 11, 2026</p>

<h3>1. Purpose</h3>
<p>This policy establishes our ethical commitments as a residential‑facing software company and the environmental accountability measures that accompany our growth. As our platform scales across residential properties, our responsibility to act transparently and proactively scales with it.</p>

<h3>2. Ethics Commitment</h3>
<p>We commit to operating with transparency about the resources our software consumes, including downstream environmental costs of the data center infrastructure we rely on. We recognize that residential deployment means our impact is tied to real neighborhoods, real buildings, and real communities. We hold ourselves accountable for the portion of that impact we can reasonably influence, and we will not overstate our capacity to solve problems beyond our size or expertise.</p>
<p>Our ethics commitments include:</p>
<ul>
  <li>Operate with transparency about environmental impact tied to product usage and scale.</li>
  <li>Take direct accountability for our own footprint rather than relying solely on third‑party infrastructure providers' commitments.</li>
  <li>Avoid overcommitting to initiatives outside our operational capacity or expertise.</li>
  <li>Report progress on environmental commitments as part of regular company governance.</li>
</ul>

<h3>3. Environmental Commitment</h3>
<p>We recognize that the processing infrastructure powering our residential software has a real water footprint, primarily through data center cooling. Rather than treating this as solely our infrastructure partners' responsibility, we commit to a local, community‑focused response: supporting water access and water quality initiatives in the residential communities where our software is deployed.</p>

<h3>3.1. Local Focus</h3>
<p>Our commitment is intentionally scoped to the communities where our residential software is deployed, rather than broad, unfocused environmental giving. This keeps our contribution proportional to our actual footprint and lets us verify real, local outcomes.</p>

<h3>3.2. Scaling With Growth</h3>
<p>As adoption grows, our environmental contribution grows proportionally.</p>
<ul>
  <li>Environmental contribution is calculated as a percentage of revenue, scaling automatically as the platform grows.</li>
  <li>Funding is directed toward verified local water access and water quality projects in communities where the software for residential users is deployed.</li>
  <li>Partner organizations are selected using independent verification standards (e.g., Gold Standard, Green‑e, or comparable certification).</li>
</ul>

<h3>4. Commitment Structure</h3>
<p>2% of gross subscription revenue is allocated to local water access and quality initiatives in communities where the software for residential users is deployed. Revenue is defined as the actual subscription price collected per seat — list price on the Service (referral) path, and discounted price on the Amenity (bulk) path. The 2% is calculated before partner share distributions, ensuring the full commitment is met regardless of distribution model.</p>

<h3>4.1. Financial Validation</h3>
<p>The 2% allocation has been validated against internal economics across all plan tiers and discount levels, confirming no plan tier places the $2.00 unit margin floor at risk. The commitment is fully funded from Outlne's margin without cross‑subsidisation.</p>

<h3>4.2. Contribution Scaling Projection</h3>
<p>Based on the economics model, contribution per 100 active seats (blended average across plans): approximately $108/month, or $1,296/year. Scaling is linear with seat growth; no fixed cap applies.</p>

<h3>5. Governance & Review</h3>
<p>This policy is reviewed quarterly alongside financial reporting, with a formal annual review. Review triggers include material changes to unit economics, expansion into new markets, availability of more granular water consumption data, and partner community feedback. Allocation percentages may be adjusted upward as margins strengthen, but will not be reduced below 2% without a formal policy amendment approved by company leadership and communicated transparently.</p>

<h3>6. Reporting</h3>
<p>An annual public‑facing summary will disclose:</p>
<ul>
  <li>Total revenue allocated under this policy.</li>
  <li>Funds deployed by community and project type.</li>
  <li>Partner organizations selected and verification standards applied.</li>
  <li>Outcomes achieved, where measurable, or progress indicators where outcomes are longer‑term.</li>
</ul>
<p>The first annual report will cover the period from policy adoption through the subsequent fiscal year end.</p>`
};

function getPolicyText(policyId) {
  return POLICY_TEXTS[policyId] || '<p>Policy text not available.</p>';
}

export default function Policies() {
  useEffect(() => {
    document.title = "Policy Center | Outlne";
  }, []);

  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);

  const lastGridChangeTime = useRef(0);
  const bgIntervalRef = useRef(null);
  const bgAnimationRef = useRef(null);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(null);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1224;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      setWindowWidth(width);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Page load effect - fade out grid and fade in background
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Optimized background slideshow
  useEffect(() => {
    const preloadImages = () => {
      backgrounds.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };
    preloadImages();

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';
      
      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);
      
      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    if (pageLoaded) {
      changeBackground();
      bgIntervalRef.current = setInterval(changeBackground, 8000);
    }

    return () => {
      clearInterval(bgIntervalRef.current);
      if (bgAnimationRef.current) {
        cancelAnimationFrame(bgAnimationRef.current);
      }
    };
  }, [pageLoaded]);

  // Grid animation - only run after page has loaded
  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      
      const timeSinceChange = time - lastGridChangeTime.current;
      
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      
      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid((prev) => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // Grid styles
  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  // Click handler: desktop/tablet uses sidebar (modal), mobile uses accordion
  const handlePolicyClick = (policyId) => {
    if (isMobile) {
      setMobileOpen(mobileOpen === policyId ? null : policyId);
    } else {
      setSelectedPolicy(policyId);
    }
  };

  // Render sidebar item
  const renderSidebarItem = (item, isActive) => (
    <div
      key={item.id}
      onClick={() => handlePolicyClick(item.id)}
      style={{
        padding: '0.75rem 1rem',
        margin: '0.2rem 0',
        cursor: 'pointer',
        borderRadius: '0.5rem',
        backgroundColor: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
        border: isActive ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent',
        transition: 'all 0.2s ease',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        color: isActive ? 'white' : '#ccc',
        fontWeight: isActive ? '500' : '400',
        fontSize: isMobile ? '0.9rem' : '0.95rem',
        fontFamily: 'avenir-lt-pro, sans-serif',
      }}
      onMouseOver={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
        }
      }}
      onMouseOut={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
        }
      }}
    >
      {item.label}
    </div>
  );

  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Optimized Background Slideshow */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        willChange: 'transform',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          transformStyle: 'preserve-3d',
        }} />
      </div>

      {/* Grid Container */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, white 1px, transparent 1px),
                linear-gradient(to bottom, white 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* Desktop & Tablet Header */}
      {!isMobile && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: '30px',
              left: '30px',
              zIndex: 20,
              cursor: 'pointer',
              fontSize: '2rem',
              color: 'white',
            }}
            onClick={() => navigate('/')}
          >
            ×
          </div>

          <div style={{
            position: 'fixed',
            top: '40px',
            right: '40px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src={LogoWhite} 
                alt="Outlne Logo"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <h1 style={{
                fontSize: '1.8rem',
                fontFamily: 'avenir-lt-pro, sans-serif',
                letterSpacing: '-1.2px',
                margin: 0,
                color: 'white',
                fontWeight: 500,
              }}>
                Outlne
              </h1>
            </div>
            <p style={{
              fontSize: '1rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>
        </>
      )}

      {/* Mobile Header with Blur Background */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '110px',
            backgroundColor: 'rgba(31, 31, 31, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 15,
          }} />
          
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 20,
            textAlign: 'left',
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              letterSpacing: '-1.2px',
              margin: 0,
              color: 'white',
              fontWeight: 500,
            }}>
              Outlne
            </h1>
            <p style={{
              fontSize: '0.9rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>

          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img 
              src={LogoWhite} 
              alt="Outlne Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div 
              style={{
                cursor: 'pointer',
                marginRight: '10px',
                fontSize: '1.8rem',
                color: 'white',
                marginTop: '-5px',
              }}
              onClick={() => navigate('/')}
            >
              ×
            </div>
          </div>
        </>
      )}

      {/* Main Content Container */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 5,
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 1s ease',
        overscrollBehavior: 'none'
      }}>
        
        {/* Content Wrapper */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          minHeight: isMobile ? 'auto' : '100vh',
          paddingTop: isMobile ? '130px' : '40px',
          paddingBottom: '0',
          position: 'relative',
          zIndex: 5,
        }}>
          
          {/* Centered Content */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: isMobile ? '100%' : '1200px',
            padding: isMobile ? '0 20px' : '0 40px',
            flex: '1',
          }}>
            
            {/* Policy Hub Header */}
            <div style={{
              textAlign: 'center',
              maxWidth: '800px',
              marginBottom: isMobile ? '2rem' : '3rem',
              width: '100%',
              marginTop: isMobile ? '0.5rem' : 70,
            }}>
              <h1 style={{
                fontFamily: 'avenir-lt-pro, sans-serif',
                fontSize: isMobile ? '1.6rem' : isTablet ? '2rem' : '2.2rem',
                fontWeight: '500',
                color: 'white',
                marginBottom: '0.8rem',
                lineHeight: '1.2',
                letterSpacing: '-0.5px'
              }}>
                Policy Center
              </h1>
              <p style={{
                color: '#ccc',
                fontSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
                lineHeight: '1.5',
                fontFamily: 'avenir-lt-pro, sans-serif',
                margin: 0
              }}>
                Select a policy from the sidebar to view its details
              </p>
            </div>

            {/* Desktop & Tablet Layout: Modal + Sidebar */}
            {!isMobile && (
              <div style={{
                display: 'flex',
                gap: '2rem',
                width: '100%',
                maxWidth: '1100px',
                marginBottom: '3rem',
                alignItems: 'stretch',
              }}>
                {/* Modal */}
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: '2rem',
                  padding: '2rem',
                  backgroundColor: 'rgba(34, 34, 34, 0.95)',
                  backdropFilter: 'blur(10px)',
                  overflowY: 'auto',
                  maxHeight: '600px',
                }}>
                  <div style={{ color: 'white', fontFamily: 'avenir-lt-pro, sans-serif' }}>
                    {selectedPolicy ? (
                      <div 
                        className="policy-content"
                        style={{ color: '#ccc', lineHeight: 1.8, fontSize: '0.95rem' }}
                        dangerouslySetInnerHTML={{ __html: getPolicyText(selectedPolicy) }}
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                        <span className="blink-text" style={{ 
                          color: 'white',
                          fontSize: '1.2rem', 
                          fontFamily: 'avenir-lt-pro, sans-serif'
                        }}>
                          Select a policy from the sidebar to view its content
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar with blue scrollbar – squared */}
                <div
                  className="sidebar-scroll"
                  style={{
                    width: '280px',
                    flexShrink: 0,
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '0',
                    padding: '1rem',
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(10px)',
                    overflowY: 'auto',
                    maxHeight: '600px',
                  }}
                >
                  <div style={{
                    fontFamily: 'avenir-lt-pro, sans-serif',
                    color: '#aaa',
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '0.5rem',
                    paddingLeft: '0.5rem',
                  }}>
                    Legal Documents
                  </div>
                  {LEGAL_DOCUMENTS.map(item => renderSidebarItem(item, selectedPolicy === item.id))}
                  
                  <div style={{
                    margin: '1.5rem 0 0.5rem',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    paddingTop: '1rem',
                    fontFamily: 'avenir-lt-pro, sans-serif',
                    color: '#aaa',
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    paddingLeft: '0.5rem',
                  }}>
                    Supporting Policies
                  </div>
                  {SUPPORTING_POLICIES.map(item => renderSidebarItem(item, selectedPolicy === item.id))}
                </div>
              </div>
            )}

            {/* Mobile Accordion */}
            {isMobile && (
              <div style={{
                width: '100%',
                maxWidth: '500px',
                marginBottom: '3rem',
              }}>
                <div style={{
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '1rem',
                  padding: '0.5rem',
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  backdropFilter: 'blur(10px)',
                }}>
                  {/* Legal Documents Section */}
                  <div style={{
                    fontFamily: 'avenir-lt-pro, sans-serif',
                    color: '#aaa',
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0.5rem 0 0.25rem 0.5rem',
                  }}>
                    Legal Documents
                  </div>
                  {LEGAL_DOCUMENTS.map(item => (
                    <div key={item.id}>
                      <div
                        onClick={() => handlePolicyClick(item.id)}
                        style={{
                          padding: '0.75rem 1rem',
                          margin: '0.2rem 0',
                          cursor: 'pointer',
                          borderRadius: '0.5rem',
                          backgroundColor: mobileOpen === item.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                          border: mobileOpen === item.id ? '2px solid #40a3ff' : '2px solid transparent',
                          color: mobileOpen === item.id ? 'white' : '#ccc',
                          fontWeight: mobileOpen === item.id ? '500' : '400',
                          fontFamily: 'avenir-lt-pro, sans-serif',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>{item.label}</span>
                        <span style={{ fontSize: '0.8rem' }}>{mobileOpen === item.id ? '−' : '+'}</span>
                      </div>
                      {mobileOpen === item.id && (
                        <div 
                          className="policy-content"
                          style={{
                            padding: '0.75rem 1rem',
                            margin: '0 0 0.5rem 0',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            borderRadius: '0.5rem',
                            color: '#ccc',
                            fontSize: '0.85rem',
                            lineHeight: 1.6,
                            fontFamily: 'avenir-lt-pro, sans-serif'
                          }}
                          dangerouslySetInnerHTML={{ __html: getPolicyText(item.id) }}
                        />
                      )}
                    </div>
                  ))}
                  
                  {/* Supporting Policies Section */}
                  <div style={{
                    fontFamily: 'avenir-lt-pro, sans-serif',
                    color: '#aaa',
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '1rem 0 0.25rem 0.5rem',
                  }}>
                    Supporting Policies
                  </div>
                  {SUPPORTING_POLICIES.map(item => (
                    <div key={item.id}>
                      <div
                        onClick={() => handlePolicyClick(item.id)}
                        style={{
                          padding: '0.75rem 1rem',
                          margin: '0.2rem 0',
                          cursor: 'pointer',
                          borderRadius: '0.5rem',
                          backgroundColor: mobileOpen === item.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                          border: mobileOpen === item.id ? '2px solid #40a3ff' : '2px solid transparent',
                          color: mobileOpen === item.id ? 'white' : '#ccc',
                          fontWeight: mobileOpen === item.id ? '500' : '400',
                          fontFamily: 'avenir-lt-pro, sans-serif',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>{item.label}</span>
                        <span style={{ fontSize: '0.8rem' }}>{mobileOpen === item.id ? '−' : '+'}</span>
                      </div>
                      {mobileOpen === item.id && (
                        <div 
                          className="policy-content"
                          style={{
                            padding: '0.75rem 1rem',
                            margin: '0 0 0.5rem 0',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            borderRadius: '0.5rem',
                            color: '#ccc',
                            fontSize: '0.85rem',
                            lineHeight: 1.6,
                            fontFamily: 'avenir-lt-pro, sans-serif'
                          }}
                          dangerouslySetInnerHTML={{ __html: getPolicyText(item.id) }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Footer */}
          <div style={{
            width: '100%',
            position: 'relative',
            zIndex: 5,
            backgroundColor: '#1a1a1a',
          }}>
            <Footer />
          </div>
        </div>
      </div>

      {/* CSS Styles */}
      <style>
        {`
          html {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
          }
          
          body {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            margin: 0;
            padding: 0;
            overscroll-behavior: none;
            -webkit-overflow-scrolling: touch;
          }
          
          #root {
            height: 100%;
            width: 100%;
            overflow: hidden;
            position: fixed;
          }
          
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }
          
          *:focus {
            outline: none;
          }
          
          div[style*="mask-image"] {
            pointer-events: none;
          }

          .blink-text {
            animation: blinkText 2s ease-in-out infinite;
          }

          @keyframes blinkText {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.2; }
          }

          .sidebar-scroll::-webkit-scrollbar {
            width: 6px;
          }
          .sidebar-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .sidebar-scroll::-webkit-scrollbar-thumb {
            background: rgba(64, 163, 255, 0.6);
            border-radius: 0;
          }
          .sidebar-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(64, 163, 255, 0.9);
          }

          .policy-content h2 {
            color: #ffffff;
            font-size: 1.8rem;
            font-weight: 700;
            margin: 0 0 0.5rem 0;
            letter-spacing: -0.02em;
          }
          .policy-content h3 {
            color: #ffffff;
            font-size: 1.2rem;
            font-weight: 700;
            margin: 1.5rem 0 0.75rem 0;
            letter-spacing: -0.01em;
          }
          .policy-content p {
            color: #e0e0e0;
            font-size: 0.95rem;
            line-height: 1.8;
            margin: 0.75rem 0;
          }
          .policy-content .last-updated {
            color: #aaaaaa;
            font-size: 0.85rem;
            margin-bottom: 1.5rem;
          }
          .policy-content ul {
            margin: 0.5rem 0 0.75rem 0;
            padding-left: 1.5rem;
          }
          .policy-content li {
            color: #e0e0e0;
            font-size: 0.95rem;
            line-height: 1.8;
            margin-bottom: 0.35rem;
          }
          .policy-content strong {
            color: #ffffff;
            font-weight: 700;
          }
        `}
      </style>
    </>
  );
}